// DetalhePrestacao.jsx
import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemText,
  Alert,
  Stack,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter
} from '@mui/material';
import { 
  ReceiptLong as ReceiptIcon, 
  VisibilityOutlined as ViewIcon,
  InfoOutlined as InfoIcon 
} from '@mui/icons-material';
import { formatarMoeda } from '../../utils/formatters';

const getStatusColor = (status) => {
  const statusColors = {
    PENDENTE: 'warning',
    PAGO: 'success',
    ATRASADO: 'error',
    ISENTO: 'info',
    BLOQUEADO: 'error',
    PROCESSANDO: 'info'
  };
  return statusColors[status] || 'default';
};

const getStatusLabel = (status) => {
  const statusLabels = {
    PENDENTE: 'Pendente',
    PAGO: 'Pago',
    ATRASADO: 'Atrasado',
    ISENTO: 'Isento',
    BLOQUEADO: 'Bloqueado',
    PROCESSANDO: 'Processando'
  };
  return statusLabels[status] || status;
};

const DetalhePrestacao = ({ prestacao }) => {
  const valorTotal = parseFloat(prestacao.valor);
  const iva = valorTotal * 0.16;
  const totalComIva = valorTotal + iva;

  const dataVencimento = new Date(prestacao.dataVencimento);
  const hoje = new Date();
  const diasAteVencimento = Math.ceil((dataVencimento - hoje) / (1000 * 60 * 60 * 24));

  return (
    <Paper elevation={0} sx={{ p: 2 }}>
      {/* Cabeçalho da Prestação */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h6">
            Prestação {prestacao.mes}
          </Typography>
          <Chip
            label={getStatusLabel(prestacao.status)}
            color={getStatusColor(prestacao.status)}
            size="small"
            sx={{ mt: 1 }}
          />
        </Box>
        <Tooltip title="Visualizar Detalhes">
          <IconButton>
            <ViewIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Valor da Prestação */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1">Valor da Prestação</Typography>
        <Typography variant="h5" gutterBottom>
          {formatarMoeda(prestacao.valor)} MZN
        </Typography>
      </Box>

      {/* Datas e Vencimento */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body1" gutterBottom>
          Data de Vencimento: {dataVencimento.toLocaleDateString()}
        </Typography>

        <Alert 
          severity={diasAteVencimento > 5 ? "info" : "warning"} 
          sx={{ mt: 1 }}
          icon={<InfoIcon />}
        >
          {diasAteVencimento > 0
            ? `Faltam ${diasAteVencimento} dias para o vencimento`
            : `Vencido há ${Math.abs(diasAteVencimento)} dias`}
        </Alert>
      </Box>

      {/* Detalhes da Fatura */}
      <Typography variant="h6" gutterBottom>
        Detalhes da Fatura
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Facturado à:
            </Typography>
            <Typography>{prestacao.vinculo?.estudante?.nome}</Typography>
            <Typography>{prestacao.vinculo?.estudante?.endereco}</Typography>
            <Typography>NUIT: {prestacao.vinculo?.estudante?.nuit || 'N/A'}</Typography>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Pagar à:
            </Typography>
            <Typography>Zenkroll, Ltd.</Typography>
            <Typography>NUIT: {prestacao.plano?.instituicao?.nuit}</Typography>
            <Typography>Email: info@zenkroll.co.mz</Typography>
            <Typography>Tel: (+258) 84 635 1111 | 86 635 1111</Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Itens da Fatura */}
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Descrição</TableCell>
              <TableCell align="right">Valor</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>
                Mensalidade {prestacao.mes}
                {prestacao.descricao && ` - ${prestacao.descricao}`}
              </TableCell>
              <TableCell align="right">{formatarMoeda(valorTotal)} MT</TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="right">Sub Total</TableCell>
              <TableCell align="right">{formatarMoeda(valorTotal)} MT</TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="right">IVA (16%)</TableCell>
              <TableCell align="right">{formatarMoeda(iva)} MT</TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">{formatarMoeda(totalComIva)} MT</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Histórico de Transações */}
      <Typography variant="subtitle1" gutterBottom>
        Histórico de Transações
      </Typography>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Data</TableCell>
              <TableCell>Método</TableCell>
              <TableCell>Referência</TableCell>
              <TableCell align="right">Valor</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(!prestacao.pagamentos || prestacao.pagamentos.length === 0) ? (
              <TableRow>
                <TableCell colSpan={4} align="center">Sem transações</TableCell>
              </TableRow>
            ) : (
              prestacao.pagamentos.map((pagamento, index) => (
                <TableRow key={index}>
                  <TableCell>
                    {new Date(pagamento.dataPagamento).toLocaleDateString()}
                  </TableCell>
                  <TableCell>{pagamento.metodoPagamento}</TableCell>
                  <TableCell>{pagamento.referencia || 'N/A'}</TableCell>
                  <TableCell align="right">
                    {formatarMoeda(pagamento.valorPago)} MT
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={3} align="right">Balanço</TableCell>
              <TableCell align="right">{formatarMoeda(totalComIva)} MT</TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>

      {/* Informações Adicionais */}
      {prestacao.observacoes && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Observações:
          </Typography>
          <Typography variant="body2">
            {prestacao.observacoes}
          </Typography>
        </Box>
      )}

      {/* Regras de Multa se Atrasado */}
      {prestacao.status === 'ATRASADO' && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Regras de Multa:
          </Typography>
          <Typography variant="body2" component="div">
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>Até 5 dias: 20% do valor</li>
              <li>Até 10 dias: 50% do valor</li>
              <li>Após 10 dias: 100% do valor</li>
            </ul>
          </Typography>
        </Alert>
      )}
    </Paper>
  );
};

DetalhePrestacao.propTypes = {
  prestacao: PropTypes.shape({
    id: PropTypes.number.isRequired,
    mes: PropTypes.string.isRequired,
    valor: PropTypes.number.isRequired,
    valorComMulta: PropTypes.number,
    status: PropTypes.oneOf([
      'PENDENTE',
      'PAGO',
      'ATRASADO',
      'ISENTO',
      'BLOQUEADO',
      'PROCESSANDO'
    ]).isRequired,
    dataVencimento: PropTypes.string.isRequired,
    descricao: PropTypes.string,
    observacoes: PropTypes.string,
    vinculo: PropTypes.shape({
      estudante: PropTypes.shape({
        nome: PropTypes.string.isRequired,
        endereco: PropTypes.string,
        nuit: PropTypes.string
      })
    }),
    plano: PropTypes.shape({
      instituicao: PropTypes.shape({
        nome: PropTypes.string.isRequired,
        nuit: PropTypes.string
      })
    }),
    pagamentos: PropTypes.arrayOf(PropTypes.shape({
      dataPagamento: PropTypes.string.isRequired,
      metodoPagamento: PropTypes.string.isRequired,
      referencia: PropTypes.string,
      valorPago: PropTypes.number.isRequired
    }))
  }).isRequired
};

export default DetalhePrestacao;